import { createFileRoute, Outlet, Link, useLocation, useNavigate } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { Settings, Users, Eye, Zap, Bell, Headphones, Gamepad2, History, Code } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export const Route = createFileRoute('/_app/settings')({
  component: SettingsLayout,
})

interface NavItem {
  icon: React.ComponentType<{ className?: string }>
  label: string
  href: string
}

interface NavGroup {
  title: string
  items: NavItem[]
}

const navGroups: NavGroup[] = [
  {
    title: 'Loja',
    items: [{ icon: Settings, label: 'Geral', href: '/settings/general' }],
  },
  {
    title: 'Programa',
    items: [
      { icon: Users, label: 'Creators', href: '/settings/creators' },
      { icon: Eye, label: 'Social Listening', href: '/settings/social-listening' },
    ],
  },
  {
    title: 'Conectar',
    items: [
      { icon: Zap, label: 'Integrações', href: '/settings/integrations' },
      { icon: Bell, label: 'Notificações', href: '/settings/notifications' },
    ],
  },
  {
    title: 'Equipe',
    items: [{ icon: Headphones, label: 'Equipe', href: '/settings/team' }],
  },
  {
    title: 'Avançado',
    items: [
      { icon: Gamepad2, label: 'Playground', href: '/settings/playground' },
      { icon: History, label: 'Histórico', href: '/settings/history' },
      { icon: Code, label: 'Desenvolvedor', href: '/settings/developers' },
    ],
  },
]

const allNavItems = navGroups.flatMap((g) => g.items)

function SettingsLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const isPlayground = location.pathname.startsWith('/settings/playground')

  const currentItem = allNavItems.find((item) => location.pathname.startsWith(item.href))

  function handleMobileSelect(value: string) {
    navigate({ to: value as never })
  }

  return (
    <div>
      {/* Mobile select */}
      <div className="md:hidden mb-4">
        <Select value={currentItem?.href} onValueChange={handleMobileSelect}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Navegar para..." />
          </SelectTrigger>
          <SelectContent>
            {navGroups.map((group) =>
              group.items.map((item) => (
                <SelectItem key={item.href} value={item.href}>
                  {item.label}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Desktop layout */}
      <div className="grid md:grid-cols-[220px_1fr] gap-0">
        {/* Sidebar */}
        <div className="hidden md:block border-r border-border/40">
          <nav className="sticky top-16 space-y-4 pr-6" aria-label="Settings navigation">
            {navGroups.map((group) => (
              <div key={group.title}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/70 mb-1.5 px-2">
                  {group.title}
                </p>
                <ul className="space-y-0.5">
                  {group.items.map((item) => {
                    const isActive = location.pathname.startsWith(item.href)
                    const Icon = item.icon
                    return (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          className={cn(
                            'flex items-center gap-2.5 px-2.5 py-1.75 text-[13px] font-medium cursor-pointer w-full',
                            isActive
                              ? 'bg-primary/10 text-primary border-l-2 border-primary rounded-r-md'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md'
                          )}
                          style={
                            isActive
                              ? {
                                  boxShadow: 'inset 0 1px 2px rgba(16, 185, 129, 0.08)',
                                  transition: 'background-color 150ms ease, color 150ms ease',
                                }
                              : { transition: 'background-color 150ms ease, color 150ms ease' }
                          }
                        >
                          <Icon className="h-3.75 w-3.75 shrink-0" />
                          {item.label}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className={cn('animate-in fade-in duration-300 pl-8', !isPlayground && 'max-w-3xl')}>
          <Outlet />
        </div>
      </div>
    </div>
  )
}
